/**
 * @file CreateCertifiedPlanCandidateDialog.jsx
 * @module ui/src/components/tenant-admin/CreateCertifiedPlanCandidateDialog
 * @description
 *   Creates a Certified Plan Candidate from an eligible approved operational plan
 *   and enters that candidate into independent template certification governance.
 *   Source-plan approval is explicitly not inherited by the new template candidate.
 *
 * @version 1.0.0
 * @since S755
 * @author data-fit SME Build · Reviewed: Claude S755
 *
 * @architecture
 *   Locked decisions : #15
 *   Temporal invariants : none
 *   Design principles : DP-10
 *   Cross-cutting contracts : none
 *
 * @domain-rules
 *   - Certified Plan Candidate creation is permanently separate from operational plan forking.
 *   - Only an approved source plan with at least one active assignment is eligible.
 *   - Source-plan approval is never inherited by the candidate.
 *   - The candidate begins as draft and enters its own certification governance.
 *   - Explicit governance acknowledgement is required before creation.
 *   - Candidate creation is server-authoritative with no optimistic success state.
 *
 * @session-6-migration-flags
 *   - none
 *
 * @changelog
 *   1.0.0  S755  Initial build — Group D batch delivery
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} CandidateSourcePlan
 * @property {number} planId
 * @property {string} planCode
 * @property {string} planName
 * @property {number} version
 * @property {string} planStatus
 * @property {number} assignmentCount
 * @property {string} roleLabel
 * @property {string} segmentLabel
 */

/**
 * @typedef {Object} CreateCertifiedPlanCandidateDialogProps
 * @property {CandidateSourcePlan} sourcePlan
 * @property {boolean} open
 * @property {(templateId: string) => void} onCreated
 * @property {() => void} onCancel
 */

const TEMPLATE_CODE_PATTERN = /^[A-Za-z0-9-]+$/;

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
    background: 'rgb(var(--color-bg))',
    zIndex: 1000,
    padding: '20px',
  },
  dialog: {
    width: 'min(720px, 100%)',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '22px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '8px',
    background: 'rgb(var(--color-surface))',
    color: 'rgb(var(--color-text))',
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    fontSize: 'var(--font-sz-base)',
  },
  heading: {
    margin: 0,
    fontSize: '21px',
  },
  muted: {
    color: 'rgb(var(--color-text-muted))',
  },
  source: {
    marginTop: '16px',
    padding: '14px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '6px',
    background: 'rgb(var(--color-surface-muted))',
  },
  field: {
    display: 'grid',
    gap: '6px',
    marginTop: '16px',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '9px 10px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '5px',
    background: 'rgb(var(--color-surface))',
    color: 'rgb(var(--color-text))',
    font: 'inherit',
  },
  textarea: {
    width: '100%',
    minHeight: '100px',
    boxSizing: 'border-box',
    padding: '9px 10px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '5px',
    background: 'rgb(var(--color-surface))',
    color: 'rgb(var(--color-text))',
    font: 'inherit',
    resize: 'vertical',
  },
  notice: {
    marginTop: '20px',
    padding: '14px',
    borderLeft: '4px solid rgb(var(--color-info))',
    background: 'rgb(var(--color-surface-muted))',
  },
  checkbox: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
    marginTop: '12px',
    fontWeight: 600,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '22px',
  },
};

function getFocusableElements(container) {
  // WHY: Modal focus is constrained to enabled interactive controls in this governance surface.
  if (!container) {
    return [];
  }

  return Array.from(
    container.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ),
  );
}

/**
 * @param {CreateCertifiedPlanCandidateDialogProps} props
 * @returns {React.ReactElement|null}
 */
export default function CreateCertifiedPlanCandidateDialog({
  sourcePlan,
  open,
  onCreated,
  onCancel,
}) {
  // WHY: Candidate name is independent template-library metadata, not inherited plan identity.
  const [templateName, setTemplateName] = useState('');

  // WHY: Candidate code is independent governed template metadata with its own format constraint.
  const [templateCode, setTemplateCode] = useState('');

  // WHY: Description records reusable design intent for future template consumers.
  const [description, setDescription] = useState('');

  // WHY: Certification rationale explains why this operational design merits institutionalization.
  const [
    certificationRationale,
    setCertificationRationale,
  ] = useState('');

  // WHY: Explicit acknowledgement prevents source-plan approval from being mistaken for candidate approval.
  const [
    governanceAcknowledged,
    setGovernanceAcknowledged,
  ] = useState(false);

  // WHY: Pending state prevents duplicate template-governance mutations.
  const [submitting, setSubmitting] = useState(false);

  // WHY: Server failures remain visible and retryable without optimistic candidate creation.
  const [error, setError] = useState(null);

  const dialogRef = useRef(null);
  const firstInputRef = useRef(null);
  const headingRef = useRef(null);

  // WHY: Eligibility is a hard source-plan guard, not merely advisory copy.
  const eligibleSource =
    sourcePlan.planStatus === 'approved' &&
    sourcePlan.assignmentCount > 0;

  // WHY: Every opening begins a fresh candidate-authoring interaction.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setTemplateName('');
    setTemplateCode('');
    setDescription('');
    setCertificationRationale('');
    setGovernanceAcknowledged(false);
    setSubmitting(false);
    setError(null);

    const focusTimer = window.setTimeout(() => {
      // WHY: Eligible source plans focus the first required field; ineligible sources
      // focus the heading because no creation fields should become the primary target.
      if (eligibleSource) {
        firstInputRef.current?.focus();
      } else {
        headingRef.current?.focus();
      }
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [open, sourcePlan.planId, eligibleSource]);

  // WHY: The candidate workflow is modal, so Escape cancels and Tab stays inside the dialog.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      // WHY: Escape delegates cancellation without creating or mutating a candidate.
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }

      // WHY: Focus trap concerns only Tab navigation.
      if (event.key !== 'Tab') {
        return;
      }

      const focusable = getFocusableElements(dialogRef.current);

      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // WHY: Reverse keyboard traversal wraps within the governance dialog.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }

      // WHY: Forward keyboard traversal wraps within the governance dialog.
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onCancel]);

  // WHY: Candidate code format is validated structurally before a creation attempt.
  const templateCodeValid =
    templateCode.trim().length > 0 &&
    TEMPLATE_CODE_PATTERN.test(templateCode.trim());

  // WHY: All certification metadata plus explicit governance acknowledgement are
  // mandatory before the template-governance mutation may be attempted.
  const formValid =
    eligibleSource &&
    templateName.trim().length > 0 &&
    templateCodeValid &&
    description.trim().length > 0 &&
    certificationRationale.trim().length > 0 &&
    governanceAcknowledged;

  // WHY: Field handlers update only local draft metadata and clear stale mutation errors.
  const handleTemplateNameChange = (event) => {
    setTemplateName(event.target.value);
    setError(null);
  };

  // WHY: Candidate code remains local until the authoritative creation request succeeds.
  const handleTemplateCodeChange = (event) => {
    setTemplateCode(event.target.value);
    setError(null);
  };

  // WHY: Description is required reusable-template context and is not derived from source metadata.
  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
    setError(null);
  };

  // WHY: Certification rationale must be explicitly authored rather than inferred by the UI.
  const handleRationaleChange = (event) => {
    setCertificationRationale(event.target.value);
    setError(null);
  };

  // WHY: Governance acknowledgement is an explicit user decision required before creation.
  const handleAcknowledgementChange = (event) => {
    setGovernanceAcknowledged(event.target.checked);
    setError(null);
  };

  // WHY: Cancel exits without mutating plan or template governance.
  const handleCancel = () => {
    if (!submitting) {
      onCancel();
    }
  };

  // WHY: Candidate creation is a separate template-governance mutation. onCreated
  // executes only when the server confirms a concrete templateId.
  const handleCreateCandidate = async () => {
    if (!formValid || submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // WHY: This template-library endpoint is intentionally distinct from the plan-fork endpoint.
      const response = await fetch(
        `/api/v1/template-library/templates/from-plan/${sourcePlan.planId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            templateName: templateName.trim(),
            templateCode: templateCode.trim(),
            description: description.trim(),
            certificationRationale: certificationRationale.trim(),
          }),
        },
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(
          payload?.detail ||
            payload?.message ||
            'The Certified Plan Candidate could not be created.',
        );
      }

      const payload = await response.json();

      if (
        typeof payload.templateId !== 'string' ||
        payload.templateId.length === 0
      ) {
        throw new Error(
          'The server did not return the template identifier.',
        );
      }

      onCreated(payload.templateId);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'The Certified Plan Candidate could not be created.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // WHY: Closed controlled dialog must not retain focus or stale certification metadata.
  if (!open) {
    return null;
  }

  return (
    <div style={styles.backdrop}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="certified-candidate-title"
        tabIndex={-1}
        style={styles.dialog}
      >
        <h2
          id="certified-candidate-title"
          ref={headingRef}
          tabIndex={-1}
          style={styles.heading}
        >
          Create Certified Plan Candidate
        </h2>

        <div style={styles.source}>
          <p>
            <strong>Source plan:</strong> {sourcePlan.planName} (V
            {sourcePlan.version})
          </p>
          <p>
            <strong>Role:</strong> {sourcePlan.roleLabel}
          </p>
          <p>
            <strong>Segment:</strong> {sourcePlan.segmentLabel}
          </p>
          <p>
            <strong>Active assignments:</strong> {sourcePlan.assignmentCount}
          </p>
        </div>

        {/* WHY: Source eligibility is a hard governance gate. Ineligible plans render
            an explanatory error state instead of exposing candidate creation controls. */}
        {!eligibleSource ? (
          <>
            <p
              className="modifier-manager-inline-error"
              role="alert"
            >
              This source plan is not eligible to create a Certified Plan
              Candidate. The source plan must be approved and have at least one
              active assignment.
            </p>

            <div style={styles.actions}>
              <button
                type="button"
                className="secondary-button"
                onClick={handleCancel}
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <label style={styles.field}>
              <span>Template Name</span>
              <input
                ref={firstInputRef}
                style={styles.input}
                type="text"
                value={templateName}
                onChange={handleTemplateNameChange}
                required
                autoComplete="off"
              />
            </label>

            <label style={styles.field}>
              <span>Template Code</span>
              <input
                style={styles.input}
                type="text"
                value={templateCode}
                onChange={handleTemplateCodeChange}
                required
                autoComplete="off"
                aria-describedby="template-code-guidance"
              />
            </label>
            <p id="template-code-guidance" style={styles.muted}>
              Use letters, numbers, and hyphens only.
            </p>

            {/* WHY: Invalid candidate-code format is structural form feedback and
                remains separate from a later server creation failure. */}
            {templateCode.trim().length > 0 && !templateCodeValid ? (
              <p className="modifier-manager-inline-error">
                Template Code may contain only letters, numbers, and hyphens.
              </p>
            ) : null}

            <label style={styles.field}>
              <span>Description</span>
              <textarea
                style={styles.textarea}
                value={description}
                onChange={handleDescriptionChange}
                required
              />
            </label>

            <label style={styles.field}>
              <span>Certification Rationale / Notes</span>
              <textarea
                style={styles.textarea}
                value={certificationRationale}
                onChange={handleRationaleChange}
                required
              />
            </label>

            <div style={styles.notice}>
              {/* WHY: Locked governance copy prevents source-plan approval from being
                  interpreted as inherited certification approval. */}
              <p>
                Creating a Certified Plan Candidate does not inherit the approval
                status of the source plan. This template will start as a draft and
                enter its own certification governance process.
              </p>

              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={governanceAcknowledged}
                  onChange={handleAcknowledgementChange}
                />
                <span>
                  I understand this template starts as a draft and requires
                  separate certification.
                </span>
              </label>
            </div>

            {/* WHY: Server mutation failures are surfaced inline and leave all authored
                candidate metadata available for correction or retry. */}
            {error ? (
              <p
                className="modifier-manager-inline-error"
                role="alert"
              >
                {error}
              </p>
            ) : null}

            <div style={styles.actions}>
              <button
                type="button"
                className="secondary-button"
                disabled={submitting}
                onClick={handleCancel}
              >
                Cancel
              </button>

              <button
                type="button"
                className="primary-button"
                disabled={!formValid || submitting}
                onClick={handleCreateCandidate}
              >
                {submitting
                  ? 'Creating candidate…'
                  : 'Create Certified Plan Candidate'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

CreateCertifiedPlanCandidateDialog.propTypes = {
  sourcePlan: PropTypes.shape({
    planId: PropTypes.number.isRequired,
    planCode: PropTypes.string.isRequired,
    planName: PropTypes.string.isRequired,
    version: PropTypes.number.isRequired,
    planStatus: PropTypes.string.isRequired,
    assignmentCount: PropTypes.number.isRequired,
    roleLabel: PropTypes.string.isRequired,
    segmentLabel: PropTypes.string.isRequired,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onCreated: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

/**
 * @example
 * <CreateCertifiedPlanCandidateDialog
 *   sourcePlan={{
 *     planId: 418,
 *     planCode: 'AE-ENT-2026',
 *     planName: 'Enterprise AE FY26',
 *     version: 3,
 *     planStatus: 'approved',
 *     assignmentCount: 842,
 *     roleLabel: 'Enterprise Account Executive',
 *     segmentLabel: 'Enterprise',
 *   }}
 *   open={candidateDialogOpen}
 *   onCreated={(templateId) => navigate(`/template-library/${templateId}`)}
 *   onCancel={() => setCandidateDialogOpen(false)}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 * - Candidate creation is permanently separate from operational plan forking.
 * - Source plan must be approved and have at least one active assignment.
 * - Locked governance notice explicitly states that source approval is not inherited.
 * - Candidate begins as draft and requires separate certification governance.
 * - Explicit acknowledgement is required before creation.
 * - Primary action is exactly "Create Certified Plan Candidate".
 * - onCreated executes only after server-confirmed candidate creation.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 * - none
 */

/**
 * ACCESSIBILITY NOTES
 * - Dialog exposes role="dialog", aria-modal, and an accessible heading.
 * - Eligible flows focus the first required field; ineligible flows focus the heading.
 * - Tab and Shift+Tab remain trapped inside the modal.
 * - Escape invokes onCancel.
 * - Template code guidance is connected with aria-describedby.
 * - Server and eligibility failures are textually expressed and use alert semantics where appropriate.
 */